import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import {
  Loader,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  NotificationStack,
  NotificationType,
} from "@transfr-inc/dashboard-components";

import {
  Button,
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownItems,
  Textarea,
  ToggleButton,
} from "@transfr-inc/dashboard-components/forms";

import { toScorePercent } from "@lib/utils/score.utils";
import {
  EXEMPT_REASON_LABELS,
  formatExemptReason,
  getExemptReasonOptions,
  parseExemptReason,
  requiresCustomText,
} from "@models/exempt-reason";

import "./exempt-user.modal.scss";

export default ({
  open,
  onClose,
  onApply,
  initialData = {},
  allowScoreEdit = false,
  allowExempt = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [notifications, setNotifications] = useState();
  const methods = useForm({
    mode: "onBlur",
  });

  const { control, handleSubmit, reset, formState, watch } = methods;

  const { isDirty, dirtyFields, errors, isValid } = formState;

  const watchExempt = watch("exempt");
  const watchExemptReason = watch("exemptReason");

  const statusOptions = [];
  const exemptReasonOptions = getExemptReasonOptions();

  // A score can only be raised, so the current one is the floor
  const currentScore =
    initialData?.masteryScore == null
      ? undefined
      : toScorePercent(initialData.masteryScore);
  const canEditScore = allowScoreEdit && currentScore !== 100;

  const onSubmit = (data) => {
    setIsLoading(true);

    const formattedExemptReason = formatExemptReason(
      data.exemptReason,
      data.customText
    );

    onApply({
      ...data,
      activityId: initialData.id,
      userId: initialData.userId,
      contentType: initialData.contentType,
      learningExperienceId: initialData.learningExperienceId,
      learningObjectiveId: initialData.learningObjectiveId,
      simulationCode: initialData.simulationCode,
      exemptReason: formattedExemptReason,
      unitId: initialData.unitId,
      lessonId: initialData.lessonId,
      // Only send a score the user actually edited. The field is seeded with
      // the current score, so sending it always would make an exempt-only
      // apply look like a manual override.
      masteryScore:
        canEditScore &&
        dirtyFields.masteryScore &&
        data.masteryScore !== "" &&
        data.masteryScore != null
          ? Number(data.masteryScore)
          : undefined,
    });
  };

  const handleCancel = () => {
    onClose();
    setIsLoading();
  };

  useEffect(() => {
    if (!open) {
      reset();
      setIsLoading();
    }
  }, [open, reset]);

  const checkErrors = () => {
    if (Object.keys(errors).length > 0) {
      const messages = new Set(Object.values(errors).map((e) => e.message));
      showError(...messages.values());
    } else {
      setNotifications();
    }
  };

  const showError = (...errors) => {
    setNotifications(
      errors.map((e) => ({
        type: NotificationType.error,
        message: e,
      }))
    );
  };

  useEffect(() => {
    if (errors) {
      checkErrors();
    }
  }, [formState]);

  useEffect(() => {
    if (!requiresCustomText(watchExemptReason)) {
      const currentCustomText = methods.getValues("customText");
      if (!currentCustomText || currentCustomText.trim() === "") {
        reset(
          (formValues) => ({
            ...formValues,
            customText: "",
          }),
          { keepDirty: true, keepTouched: true }
        );
      }
    }
  }, [watchExemptReason, reset, methods]);

  useEffect(() => {
    if (open && initialData) {
      const {
        masteryScore,
        masteryStatus,
        status,
        exempt,
        exemptReason: storedExemptReason,
        contentType,
      } = initialData;

      const { reason: parsedReason, customText: parsedCustomText } =
        parseExemptReason(storedExemptReason);

      // Same formatter the gradebook cell uses, so the modal can't show a
      // different number than the cell behind it.
      const score = masteryScore ? `${toScorePercent(masteryScore)}%` : "N/A";
      const formattedStatus = contentType === 1 ? masteryStatus : status;

      reset({
        ...initialData,
        exempt: exempt,
        exemptReason: parsedReason || storedExemptReason,
        customText: parsedCustomText || "",
        score: score,
        masteryScore:
          masteryScore == null ? "" : `${toScorePercent(masteryScore)}`,
        status: (formattedStatus || "").toUpperCase() || "—",
      });
    }
  }, [open, initialData, reset]);

  return (
    <Modal modalClassName="edit-score-modal" open={open} onClose={onClose}>
      {isLoading && <Loader fixed />}
      <ModalHeader
        className="edit-score-header"
        icon={["fa-regular", "fa-pen-to-square"]}
        title="Edit Score"
      />
      <ModalBody className="score-modal-body">
        {notifications && (
          <NotificationStack
            notifications={notifications}
            onClose={setNotifications}
          />
        )}

        <div className="score-container">
          {initialData?.unitTitle && (
            <div className="score-item">
              {initialData.unitTitle}
              <FontAwesomeIcon
                icon="fa-light fa-chevron-right"
                className="score-icon"
              />
            </div>
          )}
          {initialData?.lessonTitle && (
            <div className="score-item">
              {initialData.lessonTitle}
              <FontAwesomeIcon
                icon="fa-light fa-chevron-right"
                className="score-icon"
              />
            </div>
          )}
          <div className="activity-name">{initialData?.title}</div>
        </div>

        <div className="score-fields">
          <div
            className={clsx("field-group", {
              "field-group--disabled": !canEditScore,
            })}
          >
            <label className="field-label">Score</label>
            {canEditScore ? (
              <Controller
                control={control}
                name="masteryScore"
                rules={{
                  min: {
                    value: 0,
                    message: "Score must be between 0 and 100.",
                  },
                  max: {
                    value: 100,
                    message: "Score must be between 0 and 100.",
                  },
                  validate: (value) =>
                    value === "" ||
                    currentScore === undefined ||
                    Number(value) > currentScore ||
                    `Score can only be raised. Enter a number higher than ${currentScore}.`,
                }}
                render={({ field: { value, onChange, onBlur, ref } }) => (
                  <input
                    className="score-input"
                    type="text"
                    inputMode="numeric"
                    maxLength={3}
                    min={currentScore === undefined ? 0 : currentScore + 1}
                    max={100}
                    value={value ?? ""}
                    onChange={(e) =>
                      onChange(e.target.value.replaceAll(/\D/g, "").slice(0, 3))
                    }
                    onBlur={onBlur}
                    ref={ref}
                  />
                )}
              />
            ) : (
              <Controller
                control={control}
                name="score"
                rules={{ required: true }}
                render={({ field: { value, onChange, onBlur, ref } }) => (
                  <input
                    className="score-input"
                    type="text"
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    ref={ref}
                    disabled={true}
                  />
                )}
              />
            )}
          </div>

          <div
            className={clsx("field-group", { "field-group--disabled": true })}
          >
            <label className="field-label">Status</label>
            <Controller
              control={control}
              name="status"
              rules={{ required: true }}
              render={({ field: { value, onChange } }) => (
                <Dropdown
                  selected={value}
                  onOptionSelected={(option) => onChange(option.value)}
                  className="status-dropdown"
                  disabled={true}
                >
                  <DropdownButton className="status-dropdown-button">
                    <div className="status-display">{value}</div>
                  </DropdownButton>
                  <DropdownItems>
                    {statusOptions.map((option) => (
                      <DropdownItem key={option.value} value={option}>
                        <div className="status-option">
                          <FontAwesomeIcon
                            icon={["fa-solid", option.icon]}
                            className="status-icon"
                          />
                          {option.label}
                        </div>
                      </DropdownItem>
                    ))}
                  </DropdownItems>
                </Dropdown>
              )}
            />
          </div>

          <div
            className={clsx("field-group", {
              "field-group--disabled": !allowExempt,
            })}
          >
            <label className="field-label">Exempt</label>
            <Controller
              control={control}
              name="exempt"
              render={({ field: { value, onChange } }) => (
                <ToggleButton
                  value={value}
                  onChange={onChange}
                  affirmText="YES"
                  negateText="NO"
                  className={clsx("exempt-toggle", { exempted: value })}
                  disabled={!allowExempt}
                />
              )}
            />
          </div>
        </div>
        {watchExempt && (
          <div className="exempt-reason">
            <label className="reason-label">
              Why are you excusing this student?*
            </label>
            <Controller
              control={control}
              name="exemptReason"
              rules={{
                required: {
                  value: true,
                  message: "Exempt reason is required.",
                },
                validate: (value) => {
                  if (watch("exempt") && !value) {
                    return { isValid: false, error: "Please select a reason" };
                  }
                  return { isValid: true, error: "" };
                },
              }}
              render={({ field: { value, onChange, onBlur } }) => (
                <>
                  <Dropdown
                    className="exempt-reason-dropdown"
                    selected={value}
                    onOptionSelected={(option) => {
                      onChange(option.value);
                      onBlur();
                    }}
                  >
                    <DropdownButton className="exempt-reason-dropdown-button">
                      <div className="exempt-reason-display">
                        {EXEMPT_REASON_LABELS[value] ||
                          "Select an exempt reason..."}
                      </div>
                    </DropdownButton>
                    <DropdownItems>
                      {exemptReasonOptions.map((option, index) => (
                        <DropdownItem
                          key={`exempt-reason-${index}`}
                          value={option}
                          placeholder="Select an exempt reason..."
                        >
                          <div className="exempt-reason-option">
                            {option.label}
                          </div>
                        </DropdownItem>
                      ))}
                    </DropdownItems>
                  </Dropdown>
                </>
              )}
            />
            {requiresCustomText(watchExemptReason) && (
              <Controller
                control={control}
                name="customText"
                rules={{
                  required: {
                    value: true,
                    message:
                      "Additional details are required when 'Other' is selected.",
                  },
                  minLength: {
                    value: 20,
                    message:
                      "Additional details must be at least 20 characters long.",
                  },
                  maxLength: {
                    value: 500,
                    message:
                      "Additional details must be 500 characters or less.",
                  },
                  validate: (value) => {
                    if (watchExemptReason === "other" && value?.trim()) {
                      return true;
                    }
                    return true;
                  },
                }}
                render={({ field: { value, onChange, onBlur } }) => (
                  <>
                    <Textarea
                      className="exempt-reason-input"
                      type="text"
                      value={value || ""}
                      onChange={onChange}
                      onBlur={onBlur}
                      placeholder="Type to add more details here"
                      required
                    />
                  </>
                )}
              />
            )}
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        <Button
          disabled={isLoading}
          onClick={handleCancel}
          className="cancel-button"
        >
          Cancel
        </Button>
        <Button
          primary
          onClick={handleSubmit(onSubmit)}
          className="apply-button"
          disabled={!isDirty || isLoading || !isValid}
        >
          Apply
        </Button>
      </ModalFooter>
    </Modal>
  );
};
